"use client";

import * as React from "react";
import styled, { css } from "styled-components";
import defaultTheme from "../defaultTheme";
import Button from "../Button";
import ButtonLink from "../ButtonLink";
import FormLabel from "../FormLabel";
import ErrorFormTooltip from "../ErrorFormTooltip";
import Attachment from "../icons/Attachment";
import CloseCircle from "../icons/CloseCircle";
import { rtlSpacing } from "../utils/rtl";
import getSpacingToken from "../common/getSpacingToken";
import getFieldDataState from "../common/getFieldDataState";
import formElementFocus from "../InputField/helpers/formElementFocus";
import useErrorTooltip from "../ErrorFormTooltip/hooks/useErrorTooltip";
import mq from "../utils/mediaQuery";
const Field = styled.label.withConfig({
  displayName: "InputFile__Field",
  componentId: "sc-1kn5x5o-0"
})(["", ""], ({
  theme,
  $width
}) => css(["font-family:", ";display:block;position:relative;width:", ";margin-bottom:", ";"], theme.orbit.fontFamily, $width, getSpacingToken));
Field.defaultProps = {
  theme: defaultTheme
};
const FakeInput = styled(({
  children,
  className
}) => /*#__PURE__*/React.createElement("div", {
  className: className
}, children)).withConfig({
  displayName: "InputFile__FakeInput",
  componentId: "sc-1kn5x5o-1"
})(["", ""], ({
  theme,
  $disabled,
  error
}) => css(["box-sizing:border-box;display:flex;align-items:center;padding:", ";height:", ";box-shadow:inset 0 0 0 ", ";background-color:$ ", ";transition:box-shadow ", " ease-in-out;border-radius:", ";", ";", ";&:hover{box-shadow:inset 0 0 0 ", ";}"], rtlSpacing(theme.orbit.paddingInputFile), theme.orbit.heightInputNormal, `${theme.orbit.borderWidthInput} ${error ? theme.orbit.borderColorInputError : theme.orbit.borderColorInput}`, theme.backgroundInput, theme.orbit.durationFast, theme.orbit.borderRadiusNormal, mq.tablet(css(["border-radius:", ";"], theme.orbit.borderRadiusNormal)), $disabled && css(["cursor:not-allowed;background-color:", ";"], theme.orbit.backgroundInputDisabled), `${theme.orbit.borderWidthInput} ${error ? theme.orbit.paletteRedNormalHover : theme.orbit.borderColorInputHover}`));
FakeInput.defaultProps = {
  theme: defaultTheme
};

// we need to hide the input, but not with display or visibility so we can trigger the focus
const Input = styled.input.withConfig({
  displayName: "InputFile__Input",
  componentId: "sc-1kn5x5o-2"
})(["opacity:0;position:absolute;height:0;pointer-events:none;&:focus ~ ", "{", "}"], FakeInput, formElementFocus);
Input.defaultProps = {
  theme: defaultTheme
};
const getFileInputColor = ({
  error,
  fileName
}, theme) => {
  if (error) {
    return theme.orbit.paletteRedNormal;
  }
  if (fileName) {
    return theme.orbit.colorTextInput;
  }
  return theme.orbit.paletteInkNormal;
};
const StyledFileInput = styled.div.withConfig({
  displayName: "InputFile__StyledFileInput",
  componentId: "sc-1kn5x5o-3"
})(["font-family:", ";color:", ";width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:", ";"], ({
  theme
}) => theme.orbit.fontFamily, ({
  error,
  fileName,
  theme
}) => getFileInputColor({
  error,
  fileName
}, theme), ({
  theme
}) => rtlSpacing(`0 0 0 ${theme.orbit.spaceSmall}`));
StyledFileInput.defaultProps = {
  theme: defaultTheme
};
const InputFile = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    placeholder = "No file selected",
    buttonLabel = "Select file",
    required,
    onRemoveFile,
    dataTest,
    id,
    helpClosable = true,
    spaceAfter,
    width = "100%",
    help,
    error,
    onFocus,
    onBlur,
    name,
    label,
    multiple,
    onChange,
    disabled = false,
    allowedFileTypes,
    tabIndex,
    fileName,
    insideInputGroup
  } = props;
  const hasTooltip = Boolean(error || help);
  const {
    tooltipShown,
    tooltipShownHover,
    setTooltipShown,
    setTooltipShownHover,
    labelRef,
    iconRef,
    handleFocus
  } = useErrorTooltip({
    onFocus,
    hasTooltip
  });
  const shown = tooltipShown || tooltipShownHover;
  return /*#__PURE__*/React.createElement(Field, {
    spaceAfter: spaceAfter,
    ref: labelRef,
    $width: width,
    onClick: e => {
      if (iconRef && iconRef.current && iconRef.current.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, /*#__PURE__*/React.createElement(Input, {
    "data-test": dataTest,
    id: id,
    disabled: disabled,
    "aria-required": required,
    multiple: multiple,
    "data-state": insideInputGroup && typeof error === "undefined" ? undefined : getFieldDataState(!!props.error),
    type: "file",
    name: name,
    error: error,
    onChange: onChange,
    onFocus: handleFocus,
    onBlur: onBlur,
    accept: Array.isArray(allowedFileTypes) ? allowedFileTypes.join(",") : allowedFileTypes,
    tabIndex: tabIndex ? Number(tabIndex) : undefined
  }), label && /*#__PURE__*/React.createElement(FormLabel, {
    filled: !!fileName,
    error: !!error,
    help: !!help,
    labelRef: labelRef,
    iconRef: iconRef,
    onMouseEnter: () => setTooltipShownHover(true),
    onMouseLeave: () => setTooltipShownHover(false)
  }, label), /*#__PURE__*/React.createElement(FakeInput, {
    error: error,
    $disabled: disabled
  }, /*#__PURE__*/React.createElement(Button, {
    type: "secondary",
    disabled: disabled,
    size: "small",
    iconLeft: /*#__PURE__*/React.createElement(Attachment, null),
    asComponent: "div",
    role: "button"
  }, buttonLabel), /*#__PURE__*/React.createElement(StyledFileInput, {
    fileName: fileName,
    error: error,
    ref: ref
  }, fileName || placeholder), fileName && /*#__PURE__*/React.createElement(ButtonLink, {
    type: "primary",
    disabled: disabled,
    compact: true,
    iconLeft: /*#__PURE__*/React.createElement(CloseCircle, {
      ariaLabel: "remove",
      color: "secondary"
    }),
    onClick: ev => {
      ev.preventDefault();
      if (onRemoveFile) {
        onRemoveFile();
      }
    }
  })), !insideInputGroup && hasTooltip && /*#__PURE__*/React.createElement(ErrorFormTooltip, {
    help: help,
    error: error,
    helpClosable: helpClosable,
    inputSize: "normal",
    referenceElement: labelRef,
    shown: shown,
    onShown: setTooltipShown
  }));
});
InputFile.displayName = "InputFile";
export default InputFile;